import BudgetProgress from 'app/shared-components/ui/BudgetProgress';
import React, { useMemo, useState, useEffect } from 'react';
import { CARD_DIMENSIONS } from 'app/pages/mobile/utils/constants';
import { checkCardType } from 'app/shared-components/cards/CardTypes';
import { useAppSelector, useAppDispatch } from 'app/store';
import { HNCardFormFactor, HNPaymentCardStatus, HNPaymentCardClientTokenPermission } from 'API';
import type { GetCardsPayload } from 'CDK_API';
import MobileCardBackground from 'app/shared-components/cards/assets/example.svg';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { generatePaymentCardClientToken } from 'app/pages/store/CardDataSlice/cardsThunks';
import { ReactComponent as CardLockIcon } from 'assets/card-lock-icon.svg';
import { ReactComponent as MasterCardLogo } from 'assets/master-card-logo-mobile.svg';
import { Box, Typography, useTheme, styled, IconButton, Theme } from '@mui/material';
import { ReactComponent as SpeedchainLogoMobile } from 'assets/speedchain-logo-mobile.svg';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useHighnoteCardViewer } from 'app/pages/mobile/hooks/useHighnoteCardViewer';
import { sentryMask } from 'sentryReplayPrivacy';

// Styled components for card states
const StateContainer = styled(Box)(({ theme }) => ({
  width: CARD_DIMENSIONS.viewportWidth,
  maxWidth: CARD_DIMENSIONS.maxWidth,
  aspectRatio: String(CARD_DIMENSIONS.aspectRatio),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: CARD_DIMENSIONS.borderRadius,
}));

const LoadingContainer = styled(StateContainer)(({ theme }) => ({
  border: '1px solid rgba(255, 255, 255, 0.3)',
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(10px)',
}));

const ErrorContainer = styled(StateContainer)(({ theme }) => ({
  border: '1px solid rgba(255, 100, 100, 0.3)',
  background: 'rgba(50, 0, 0, 0.5)',
  backdropFilter: 'blur(10px)',
  flexDirection: 'column',
  padding: theme.spacing(3),
}));

interface CardContainerProps {
  isLocked?: boolean;
  blurAmount?: number;
}

const LockOverlay = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLocked' && prop !== 'blurAmount',
})<CardContainerProps>(({ theme, isLocked, blurAmount }) => ({
  width: CARD_DIMENSIONS.viewportWidth,
  maxWidth: CARD_DIMENSIONS.maxWidth,
  aspectRatio: String(CARD_DIMENSIONS.aspectRatio),
  position: 'relative',
  borderRadius: CARD_DIMENSIONS.borderRadius,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  boxShadow: theme.shadows[3],
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backdropFilter: blurAmount ? `blur(${blurAmount}px)` : 'none',
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

const CardSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: 8,
});

interface MobileCardProps {
  variant?: 'loading' | 'error' | 'empty' | 'default';
  paymentCard?: GetCardsPayload;
  onCardClick?: (paymentCardId: string) => void;
  index?: number;
  isActive?: boolean;
  isPeekCard?: boolean;
  isDragging?: boolean;
  getCardTransform?: (index: number) => string;
  onCardChange?: (index: number) => void;
  setCurrentIndex?: (index: number) => void;
  error?: string;
  disableBlur?: boolean;
  hideBudgetProgress?: boolean;
  hideFullDetails?: boolean;
  // Highnote SDK props for showing full card number
  isUsersCard?: boolean;
  showFullCardNumber?: boolean;
  useFullMaskedFormat?: boolean; // true = "•••• •••• •••• 1234", false = "* 1234"
  onTokenLoadingChange?: (isLoading: boolean) => void;
}

const MobileCard: React.FC<MobileCardProps> = ({
  variant = 'default',
  paymentCard,
  index = 0,
  isActive = true,
  isPeekCard = false,
  isDragging = false,
  getCardTransform = () => 'none',
  onCardClick,
  error,
  disableBlur = false,
  hideBudgetProgress = false,
  hideFullDetails = false,
  isUsersCard = false,
  showFullCardNumber = false,
  useFullMaskedFormat = false,
  onTokenLoadingChange,
}) => {
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { profile } = useAppSelector(selectUserProfile);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // State for Highnote client token
  const [clientToken, setClientToken] = useState<string>('');
  const [tokenError, setTokenError] = useState<string>('');

  // Generate client token when needed
  useEffect(() => {
    if (isUsersCard && showFullCardNumber && paymentCard?.paymentCardId && profile?.id && !clientToken) {
      onTokenLoadingChange?.(true);
      setTokenError('');
      dispatch(
        generatePaymentCardClientToken({
          paymentCardId: paymentCard.paymentCardId,
          permissions: [HNPaymentCardClientTokenPermission.READ_RESTRICTED_DETAILS],
        })
      )
        .unwrap()
        .then((token) => {
          if (token) {
            setClientToken(token);
            setTokenError('');
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to generate client token:', error);
          setTokenError('Unable to load card details. Please try again.');
        })
        .finally(() => {
          onTokenLoadingChange?.(false);
        });
    }
  }, [
    isUsersCard,
    showFullCardNumber,
    paymentCard?.paymentCardId,
    profile?.id,
    clientToken,
    dispatch,
    onTokenLoadingChange,
  ]);

  const defaultCardType = useMemo(
    () => checkCardType(integration?.highnoteProduct?.type, paymentCard?.formFactor, 'mobile'),
    [integration?.highnoteProduct?.type, paymentCard?.formFactor]
  );

  const textColor = defaultCardType.textColor === 'light' ? 'white' : theme.palette.text.secondary;

  // Handle token expiration by clearing token to trigger regeneration
  const handleTokenExpired = () => {
    setClientToken('');
    setTokenError('Card details expired. Please try again.');
  };

  // Use Highnote SDK hook
  const { showCardInfo, cardNumberVisible, toggleCardNumberVisibility } = useHighnoteCardViewer({
    clientToken,
    paymentCardId: paymentCard?.paymentCardId,
    isUsersCard,
    showFullCardNumber,
    styles: {
      color: textColor,
    },
    enableClipboard: false,
    onTokenExpired: handleTokenExpired,
  });

  const isCardLocked =
    paymentCard?.status && ['LOCKED', 'SUSPENDED', 'ACTIVATION_REQUIRED'].includes(paymentCard.status);

  // Lock icon should only show for actually locked/suspended cards, not activation required
  const shouldShowLockIcon = paymentCard?.status && ['LOCKED', 'SUSPENDED'].includes(paymentCard.status);

  // Determine blur amount based on card status
  const getBlurAmount = () => {
    if (disableBlur) return 0; // Disable blur if explicitly requested
    if (!paymentCard?.status) return 0;
    if (['LOCKED', 'SUSPENDED'].includes(paymentCard.status)) return 3; // Light blur for locked cards
    if (paymentCard.status === 'ACTIVATION_REQUIRED') return 3; // Light blur for unactivated cards
    return 0; // No blur for active cards
  };

  const formattedExpirationDate = useMemo(
    () =>
      paymentCard?.expirationDate
        ? new Date(paymentCard.expirationDate).toLocaleDateString('en-US', {
            month: '2-digit',
            year: '2-digit',
          })
        : '00/00',
    [paymentCard?.expirationDate]
  );

  if (variant === 'loading') {
    return (
      <LoadingContainer>
        <Typography variant="body1" sx={{ color: 'common.white', opacity: 0.8 }}>
          Loading cards...
        </Typography>
      </LoadingContainer>
    );
  }

  if (variant === 'error') {
    return (
      <ErrorContainer>
        <Typography variant="body1" sx={{ color: 'error.light', mb: 1, textAlign: 'center' }}>
          Unable to load cards
        </Typography>
        {error && (
          <Typography variant="body2" sx={{ color: 'common.white', opacity: 0.6, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
      </ErrorContainer>
    );
  }

  if (variant === 'empty') {
    // Create a mock card that looks like a real card
    const mockCard: GetCardsPayload = {
      paymentCardId: 'mock-card',
      paidolId: 'mock-paidol',
      formFactor: HNCardFormFactor.PHYSICAL,
      name: 'Field Card 1',
      last4: '0000',
      expirationDate: '2025-12-31T00:00:00.000Z',
      status: HNPaymentCardStatus.ACTIVE,
      spent: {
        value: 0,
        currencyCode: 'USD',
      },
      procoreEmployee: null,
      cardholder: null,
      cardGroup: null,
      spxSpendRule: null,
      monthlySpendLimit: null,
      maximumTransactionAmount: null,
      isAdminLocked: false,
    };

    return (
      <CardContainer
        role="tabpanel"
        id="empty-card-panel"
        aria-label="No cards available"
        tabIndex={0}
        sx={{
          backgroundImage: `url(${MobileCardBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backdropFilter: 'blur(3px)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }}
      >
        <CardSection>
          {/* Card header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedchainLogoMobile style={{ height: 18, width: 'auto' }} />
              <Typography variant="large" sx={{ color: 'common.white', fontSize: 16 }}>
                {mockCard.name}
              </Typography>
            </Box>
            {!hideFullDetails && (
              <Typography variant="small" sx={{ color: 'secondary.main', fontWeight: 600, fontSize: 14 }}>
                Full Details
              </Typography>
            )}
          </Box>

          {/* Card number */}
          <Typography
            variant="h5"
            sx={{
              color: textColor,
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              letterSpacing: '-1px',
              opacity: 0.9,
              fontSize: 20,
            }}
          >
            * {mockCard.last4}
          </Typography>
        </CardSection>

        <CardSection sx={{ justifyContent: 'flex-end' }}>
          {/* Budget Progress - show empty state */}
          <Box>
            <BudgetProgress {...getBudgetProgressProps(mockCard, defaultCardType, theme)} />
          </Box>

          {/* Card footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Expiration date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="medium" sx={{ color: 'common.white', opacity: 0.6, fontSize: 14 }}>
                Exp{' '}
              </Typography>
              <Typography variant="medium" sx={{ color: 'common.white', fontSize: 14 }}>
                {new Date(mockCard.expirationDate).toLocaleDateString('en-US', {
                  month: '2-digit',
                  year: '2-digit',
                })}
              </Typography>
            </Box>
            <MasterCardLogo style={{ height: 24, width: 'auto' }} />
          </Box>
        </CardSection>
      </CardContainer>
    );
  }

  if (!paymentCard) return null;

  return (
    <CardContainer
      role="tabpanel"
      id={`card-panel-${index}`}
      aria-label={`Card: ${paymentCard.name || 'Field Card'}${
        isCardLocked ? ', locked' : `, ending in ${paymentCard.last4 || '1234'}`
      }`}
      aria-hidden={!isActive}
      tabIndex={isActive ? 0 : -1}
      isLocked={isCardLocked}
      blurAmount={getBlurAmount()}
      sx={{
        transform: `${getCardTransform(index)} scale(${isActive ? 1 : isPeekCard ? 0.9 : 0.85})`,
        opacity: isActive ? 1 : isPeekCard ? 1 : 0.3,
        transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
        willChange: 'transform',
        filter: isPeekCard ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))' : 'none',
        backgroundImage: `url(${defaultCardType.cardImage})`,
      }}
      onClick={() => {
        if (onCardClick && paymentCard.paymentCardId) {
          onCardClick(paymentCard.paymentCardId);
        }
      }}
    >
      <CardSection>
        {/* Card header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedchainLogoMobile style={{ height: 18, width: 'auto' }} />
            <Typography variant="large" sx={{ color: 'common.white', fontSize: 16 }} {...sentryMask}>
              {paymentCard.name || 'Field Card'}
            </Typography>
          </Box>
          {!hideFullDetails && (
            <Typography variant="small" sx={{ color: 'secondary.main', fontWeight: 600, fontSize: 14 }}>
              Full Details
            </Typography>
          )}
        </Box>

        {/* Card number */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pt: 0.5 }}>
          {tokenError ? (
            <Typography
              variant="body2"
              sx={{
                color: 'error.light',
                fontSize: 14,
                lineHeight: 1.2,
                maxWidth: '200px',
              }}
            >
              {tokenError}
            </Typography>
          ) : !showCardInfo ? (
            <Typography
              variant="h5"
              sx={{
                color: textColor,
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 600,
                letterSpacing: '-1px',
                opacity: 0.9,
                fontSize: 20,
                lineHeight: 1.2,
              }}
            >
              {isCardLocked
                ? useFullMaskedFormat
                  ? '•••• •••• •••• ••••'
                  : '•••• ••••'
                : useFullMaskedFormat
                ? paymentCard.last4
                  ? `•••• •••• •••• ${paymentCard.last4}`
                  : `•••• •••• •••• 123${index + 4}`
                : paymentCard.last4
                ? `•••• ${paymentCard.last4}`
                : `•••• 123${index + 4}`}
            </Typography>
          ) : (
            <div
              id={`cardNumber-${paymentCard.paymentCardId}`}
              style={{
                height: '50px',
                minWidth: '150px',
                display: 'flex',
                alignItems: 'flex-start',
              }}
            />
          )}
          {isUsersCard && showFullCardNumber && (
            <IconButton
              sx={{
                color: textColor,
                backgroundColor: 'transparent !important',
                p: 0.5,
                mt: '-2px',
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                toggleCardNumberVisibility();
              }}
              size="medium"
            >
              {cardNumberVisible ? (
                <VisibilityOffOutlinedIcon fontSize="medium" />
              ) : (
                <VisibilityOutlinedIcon fontSize="medium" />
              )}
            </IconButton>
          )}
        </Box>
      </CardSection>

      <CardSection sx={{ justifyContent: 'flex-end' }}>
        {/* Budget Progress */}
        {!hideBudgetProgress && (
          <Box>
            <BudgetProgress {...getBudgetProgressProps(paymentCard, defaultCardType, theme)} />
          </Box>
        )}

        {/* Card footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Group Expiration date and CVV together */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Expiration date - show placeholder or SDK */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                variant="medium"
                sx={{ color: 'common.white', opacity: 0.6, fontSize: 16, fontFamily: 'Roboto, sans-serif' }}
              >
                Exp{' '}
              </Typography>
              {!(isUsersCard && showFullCardNumber) ? (
                <Typography
                  variant="medium"
                  sx={{
                    color: 'common.white',
                    fontSize: 16,
                    fontWeight: 550,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                  {...sentryMask}
                >
                  {formattedExpirationDate}
                </Typography>
              ) : !showCardInfo ? (
                <Typography
                  variant="medium"
                  sx={{
                    color: 'common.white',
                    fontSize: 16,
                    fontWeight: 550,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                >
                  ••/••
                </Typography>
              ) : (
                <div
                  id={`expirationDate-${paymentCard.paymentCardId}`}
                  style={{
                    minWidth: '45px',
                    width: '45px',
                    height: '19px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    color: textColor,
                    fontSize: '16px',
                    fontFamily: 'Roboto, sans-serif',
                    lineHeight: '19px',
                    marginLeft: '-4px', // Adjust alignment with "Exp" label to match spacing of Typography variant
                  }}
                />
              )}
            </Box>
            {/* CVV - show placeholder or SDK */}
            {isUsersCard && showFullCardNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="medium"
                  sx={{ color: 'common.white', opacity: 0.6, fontSize: 16, fontFamily: 'Roboto, sans-serif' }}
                >
                  Code{' '}
                </Typography>
                {!showCardInfo ? (
                  <Typography
                    variant="medium"
                    sx={{
                      color: 'common.white',
                      fontSize: 20,
                      fontWeight: 500,
                      fontFamily: 'Roboto, sans-serif',
                    }}
                  >
                    •••
                  </Typography>
                ) : (
                  <div
                    id={`cvv-${paymentCard.paymentCardId}`}
                    style={{
                      minWidth: '40px',
                      width: '40px',
                      height: '19px',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      color: textColor,
                      fontSize: '14px',
                      fontFamily: 'Roboto, sans-serif',
                      lineHeight: '20px',
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <MasterCardLogo style={{ height: 24, width: 'auto' }} />
        </Box>
      </CardSection>

      {shouldShowLockIcon && (
        <LockOverlay>
          <CardLockIcon />
        </LockOverlay>
      )}
    </CardContainer>
  );
};

const getBudgetProgressProps = (
  paymentCard: GetCardsPayload,
  defaultCardType: { textColor: string },
  theme: Theme
) => ({
  isUnlimited: !paymentCard.monthlySpendLimit?.amount,
  budget: paymentCard.monthlySpendLimit?.amount
    ? {
        value: paymentCard.monthlySpendLimit.amount,
        currency: paymentCard.monthlySpendLimit?.currency || 'USD',
      }
    : undefined,
  spent: {
    value: paymentCard.spent?.value || 0,
    currency: paymentCard.spent?.currencyCode || 'USD',
  },
  dark: defaultCardType.textColor !== 'dark',
  sx: {
    // Hide only the bottom helper text (the "unlimited" part)
    '& > *:last-child': {
      display: 'none',
    },
    // Make the progress bar taller and use secondary main gradient
    '& .MuiLinearProgress-root': {
      height: '6px',
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      borderRadius: '9px',
      backdropFilter: 'blur(2.1213676929473877px)',
      transition: 'none',
      '& .MuiLinearProgress-bar': {
        background: `linear-gradient(90deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
        borderRadius: '9px',
        transition: 'none',
      },
    },
    // Move amount and spent text to the right
    '& > div:first-of-type': {
      justifyContent: 'flex-start',
      flexDirection: 'row-reverse',
      gap: '8px',
    },
    // Style the amount to match specifications using theme
    '& .MuiTypography-root:first-of-type': {
      textAlign: 'right',
      fontFamily: theme.typography.fontFamily || 'inherit',
      fontSize: '20.412px',
      fontWeight: theme.typography.fontWeightRegular || 400,
      lineHeight: '24.948px',
    },
    // Style the "spent" text to match "Exp" color and capitalize
    '& .MuiTypography-root:last-child': {
      color: 'common.white',
      opacity: 0.6,
      textTransform: 'capitalize',
    },
  },
});

export default MobileCard;
